import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { getFrictionCategory } from '../data/mock';
import { Calendar, CheckCircle, ChevronRight } from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer
} from 'recharts';

const PROJECTION_OPTIONS = [
  { id: 'overload', text: 'une surcharge croissante pour le manager ou le dirigeant' },
  { id: 'slowdown', text: 'des projets qui avancent plus lentement que prévu' },
  { id: 'tensions', text: 'des tensions dans l\'équipe' },
  { id: 'engagement', text: 'une perte d\'engagement de certains collaborateurs' },
  { id: 'evolution', text: 'une difficulté à faire évoluer l\'organisation' }
];

const ResultScreen = ({ results, onBooking }) => {
  const [selectedProjection, setSelectedProjection] = useState(null);

  const { frictionIndex, dimensionScores } = results;
  const category = getFrictionCategory(frictionIndex);

  const radarData = [
    { dimension: 'Priorités', fluidite: 100 - (dimensionScores.clarity?.frictionScore || 50) },
    { dimension: 'Décision', fluidite: 100 - (dimensionScores.decision?.frictionScore || 50) },
    { dimension: 'Responsabilisation', fluidite: 100 - (dimensionScores.accountability?.frictionScore || 50) },
    { dimension: 'Communication', fluidite: 100 - (dimensionScores.communication?.frictionScore || 50) },
    { dimension: 'Exécution', fluidite: 100 - (dimensionScores.leader?.frictionScore || 50) }
  ];

  return (
    <div className="min-h-screen bg-[#0f1c47] py-8 px-4">
      <div className="max-w-xl mx-auto space-y-5">
        {/* Logo */}
        <div className="text-center mb-4">
          <img
            src="https://customer-assets.emergentagent.com/job_manager-inventory/artifacts/kqdhn43c_2024_10_make_it_matter.png"
            alt="WEDO"
            className="mx-auto h-14 object-contain"
          />
        </div>

        {/* Titre et Score */}
        <Card className="bg-white border-0 shadow-2xl" data-testid="result-score-card">
          <CardContent className="p-6 text-center">
            <h1 className="text-2xl font-bold text-[#0f1c47] mb-1">
              Votre diagnostic est prêt.
            </h1>
            <p className="text-gray-500 text-sm mb-5">
              Indice de friction de votre équipe
            </p>

            <div className="relative inline-flex items-center justify-center mb-4">
              <svg className="w-36 h-36 transform -rotate-90">
                <circle cx="72" cy="72" r="64" stroke="#e5e7eb" strokeWidth="10" fill="none" />
                <circle
                  cx="72" cy="72" r="64"
                  stroke={category.color}
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${(frictionIndex / 100) * 402} 402`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold" style={{ color: category.color }} data-testid="friction-score">
                  {Math.round(frictionIndex)}
                </span>
                <span className="text-gray-400 text-sm">/ 100</span>
              </div>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed">
              Ce score reflète le niveau de friction dans la dynamique actuelle de votre équipe.
            </p>
          </CardContent>
        </Card>

        {/* Radar Chart */}
        <Card className="bg-white border-0 shadow-xl" data-testid="radar-card">
          <CardContent className="p-5">
            <p className="text-gray-600 text-sm text-center mb-3">
              Votre diagnostic analyse 5 dimensions :
            </p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} margin={{ top: 15, right: 25, bottom: 15, left: 25 }}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fill: '#374151', fontSize: 10 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#9ca3af', fontSize: 9 }} />
                  <Radar name="Fluidité" dataKey="fluidite" stroke="#52a385" fill="#52a385" fillOpacity={0.4} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-gray-500 text-xs text-center mt-2">
              Le radar ci-dessus met en évidence les zones où certaines dynamiques peuvent ralentir la fluidité de votre équipe.
            </p>
          </CardContent>
        </Card>

        {/* Lecture courte */}
        <Card className="bg-white border-0 shadow-xl" data-testid="lecture-courte-card">
          <CardContent className="p-5 space-y-3">
            <p className="text-gray-700 text-sm leading-relaxed">
              Selon votre score, certaines dynamiques peuvent aujourd'hui ralentir la prise de décision, l'autonomie ou l'exécution collective.
            </p>
            <p className="text-gray-600 text-sm">
              Dans les organisations que j'accompagne, ce type de résultat correspond souvent à des situations où :
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <div className="w-1.5 h-1.5 rounded-full bg-[#e62c86] mt-2 flex-shrink-0"></div>
                certaines décisions remontent encore au manager ou au dirigeant
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <div className="w-1.5 h-1.5 rounded-full bg-[#e62c86] mt-2 flex-shrink-0"></div>
                certaines priorités ne sont pas interprétées de la même manière
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <div className="w-1.5 h-1.5 rounded-full bg-[#e62c86] mt-2 flex-shrink-0"></div>
                certaines tensions restent implicites dans l'équipe
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Insight clé */}
        <Card className="bg-white border-0 shadow-xl" data-testid="insight-card">
          <CardContent className="p-5 space-y-3">
            <p className="text-gray-700 text-sm leading-relaxed">
              Ce diagnostic donne une première lecture de la dynamique de votre équipe.
            </p>
            <p className="text-[#0f1c47] font-semibold text-sm italic">
              Dans la majorité des organisations, les causes réelles de ces frictions ne sont pas celles que l'on imagine au départ.
            </p>
          </CardContent>
        </Card>

        {/* Question de projection */}
        <Card className="bg-white border-0 shadow-xl" data-testid="projection-card">
          <CardContent className="p-6">
            <p className="text-[#e62c86] font-semibold text-sm uppercase tracking-wide mb-2 text-center">
              Question de projection
            </p>
            <h2 className="text-lg font-bold text-[#0f1c47] leading-relaxed text-center mb-5">
              Si rien ne change dans la dynamique actuelle de votre équipe, quelle serait la conséquence la plus probable dans les prochains mois ?
            </h2>
            <div className="space-y-3">
              {PROJECTION_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  data-testid={`projection-option-${option.id}`}
                  onClick={() => setSelectedProjection(option)}
                  className={`
                    w-full p-4 rounded-xl text-left transition-all duration-200
                    ${selectedProjection?.id === option.id
                      ? 'bg-[#e62c86] text-white shadow-lg'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }
                  `}
                >
                  <span className="text-sm font-medium leading-relaxed">{option.text}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Transition + Débrief — visible après sélection */}
        {selectedProjection && (
          <>
            {/* Transition */}
            <Card className="bg-white border-0 shadow-xl" data-testid="transition-card">
              <CardContent className="p-5 text-center">
                <p className="text-gray-700 font-medium text-sm">
                  C'est précisément ce type de situation que ce diagnostic permet d'anticiper.
                </p>
              </CardContent>
            </Card>

            {/* Bloc Débrief stratégique */}
            <Card className="bg-gradient-to-br from-[#0f1c47] to-[#1a2d5c] border-0 shadow-2xl text-white" data-testid="debrief-card">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-white" />
                </div>

                <h2 className="text-2xl font-bold">
                  Recevoir votre débrief stratégique
                </h2>

                <p className="text-gray-300">
                  Je vous propose un échange de 30 minutes pour analyser ce diagnostic.
                </p>

                <div className="text-left space-y-3 max-w-sm mx-auto">
                  <p className="text-gray-300 text-sm">Pendant cet échange nous regarderons :</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-[#52a385] mt-0.5 flex-shrink-0" />
                      ce que révèle réellement votre score
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-[#52a385] mt-0.5 flex-shrink-0" />
                      les mécanismes invisibles qui peuvent ralentir votre équipe
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-[#52a385] mt-0.5 flex-shrink-0" />
                      les leviers pour fluidifier la prise de décision et l'exécution collective
                    </li>
                  </ul>
                </div>

                <Button
                  data-testid="book-debrief-btn"
                  onClick={onBooking}
                  size="lg"
                  className="w-full text-[#0f1c47] py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #f3a114 0%, #e62c86 100%)' }}
                >
                  Réserver mon débrief stratégique
                  <Calendar className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm pb-4">
          WEDO Coaching & Conseil
        </p>
      </div>
    </div>
  );
};

export default ResultScreen;
